import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, CheckCircle, Mail, AlertCircle } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const PeneInvestment = () => {
  const navigate = useNavigate();
  const { user: contextUser, refreshUserData } = useUser();
  const [user, setUser] = useState(null);
  const [hasEmail, setHasEmail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [confirmingEmail, setConfirmingEmail] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    if (!contextUser || !contextUser.telegramName) {
      navigate('/login');
      return;
    }
    setUser(contextUser);
    
    // Check if user has confirmed email
    const userHasEmail = contextUser.email && contextUser.email !== '';
    setHasEmail(userHasEmail);
    if (userHasEmail) {
      setEmail(contextUser.email);
    }
    setLoading(false);
  }, [contextUser, navigate]);

  const subscriptionPlans = [
    {
      name: '1K Plan',
      deposit: '1000',
      plane: '1k',
      roi: '5%',
      monthly: '$50',
      description: 'Perfect for beginners',
      color: 'from-blue-500 to-blue-600',
      popular: false
    },
    {
      name: '1.5K Plan',
      deposit: '1500',
      plane: '1k5',
      roi: '7%',
      monthly: '$105',
      description: 'Growing your wealth',
      color: 'from-purple-500 to-purple-600',
      popular: false
    },
    {
      name: '2K Plan',
      deposit: '2000',
      plane: '2k',
      roi: '9%',
      monthly: '$180',
      description: 'Steady returns',
      color: 'from-indigo-500 to-indigo-600',
      popular: true
    },
    {
      name: '2.5K Plan',
      deposit: '2500',
      plane: '2k5',
      roi: '11%',
      monthly: '$275',
      description: 'Accelerated growth',
      color: 'from-violet-500 to-violet-600',
      popular: false
    },
    {
      name: '3K Plan',
      deposit: '3000',
      plane: '3k',
      roi: '14%',
      monthly: '$420',
      description: 'Premium investor',
      color: 'from-fuchsia-500 to-fuchsia-600',
      popular: false
    },
    {
      name: '3.5K Plan',
      deposit: '3500',
      plane: '3k5',
      roi: '18%',
      monthly: '$630',
      description: 'Maximum returns',
      color: 'from-pink-500 to-pink-600',
      popular: false
    }
  ];

  const handleConfirmEmail = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setConfirmingEmail(true);

    try {
      const configBody = {
        username: user.telegramName,
        email: email
      };

      const config = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(configBody)
      };

      const response = await fetch('https://tradebot-k4nt.onrender.com/api/payment/confirm-email', config);
      const data = await response.json();

      if (response.status === 200) {
        toast.success('Email confirmed successfully!');
        
        // Refresh user data to update email
        await refreshUserData();
        
        setHasEmail(true);
        setShowEmailModal(false);

        // Proceed to subscription if a plan was selected
        if (selectedPlan) {
          proceedToSubscription(selectedPlan);
        }
      } else {
        toast.error(data.message || 'Invalid email address');
      }
    } catch (error) {
      console.error('Email confirmation error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setConfirmingEmail(false);
    }
  };

  const proceedToSubscription = (plan) => {
    const subscriptionUrl = `https://tradebot-k4nt.onrender.com/?username=${user.telegramName}&deposit=${plan.deposit}&plane=${plan.plane}`;
    window.open(subscriptionUrl, '_blank');
  };

  const handlePlanClick = (plan) => {
    if (!hasEmail) {
      // Show email confirmation modal
      setSelectedPlan(plan);
      setShowEmailModal(true);
    } else {
      // Proceed directly to subscription
      proceedToSubscription(plan);
    }
  };

  if (loading || !user) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-700 hover:text-slate-900 mb-4"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">PENE Investment Plans</h1>
          <p className="text-slate-600">Choose the perfect investment plan to grow your wealth</p>
        </div>

        {/* Email Status Alert */}
        {!hasEmail && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-yellow-600 mt-0.5" size={20} />
              <div>
                <h3 className="font-semibold text-yellow-900 mb-1">Email Confirmation Required</h3>
                <p className="text-yellow-800 text-sm">
                  You need to confirm your email address before subscribing to an investment plan.
                  Click on any plan to set up your email.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Investment Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subscriptionPlans.map((plan, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1 relative"
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute top-4 right-4 bg-yellow-400 text-slate-900 text-xs font-bold px-3 py-1 rounded-full">
                  POPULAR
                </div>
              )}

              {/* Plan Header */}
              <div className={`bg-gradient-to-r ${plan.color} p-6 text-white`}>
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className="text-white/90 text-sm">{plan.description}</p>
              </div>

              {/* Plan Details */}
              <div className="p-6">
                <div className="mb-6">
                  <div className="flex items-baseline mb-2">
                    <span className="text-4xl font-bold text-slate-900">${plan.deposit}</span>
                    <span className="text-slate-600 ml-2">deposit</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-600 mb-4">
                    <TrendingUp size={20} />
                    <span className="text-xl font-semibold">{plan.roi} Monthly ROI</span>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-sm text-slate-600 mb-1">Estimated Monthly Returns</p>
                    <p className="text-2xl font-bold text-slate-900">{plan.monthly}</p>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="text-green-500" size={18} />
                    <span className="text-sm">Guaranteed returns</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="text-green-500" size={18} />
                    <span className="text-sm">Withdraw anytime</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="text-green-500" size={18} />
                    <span className="text-sm">24/7 support</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="text-green-500" size={18} />
                    <span className="text-sm">Secure & transparent</span>
                  </div>
                </div>

                {/* Subscribe Button */}
                <button
                  onClick={() => handlePlanClick(plan)}
                  className={`w-full bg-gradient-to-r ${plan.color} hover:opacity-90 text-white font-semibold py-3 px-6 rounded-xl transition-all`}
                >
                  {hasEmail ? 'Subscribe Now' : 'Set Email & Subscribe'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Email Confirmation Modal */}
        {showEmailModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-8">
              <div className="flex items-center gap-3 mb-6">
                <Mail className="text-blue-600" size={32} />
                <h2 className="text-2xl font-bold text-slate-900">Confirm Email</h2>
              </div>
              
              <p className="text-slate-600 mb-6">
                Please enter your email address to proceed with the {selectedPlan?.name} subscription.
              </p>

              <form onsubmit={handleConfirmEmail}>
                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@gmail.com"
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailModal(false);
                      setSelectedPlan(null);
                    }}
                    className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all"
                    disabled={confirmingEmail}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    onClick={handleConfirmEmail}
                    disabled={confirmingEmail}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-all disabled:opacity-50"
                  >
                    {confirmingEmail ? 'Confirming...' : 'Confirm & Continue'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-8 bg-gradient-to-br from-slate-900 to-slate-700 rounded-2xl p-8 text-white">
          <h2 className="text-2xl font-bold mb-4">Why PENE Investment?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <CheckCircle size={20} className="text-green-400" />
                Proven Track Record
              </h3>
              <p className="text-slate-300 text-sm">
                Our investment platform has consistently delivered returns to thousands of investors worldwide.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <CheckCircle size={20} className="text-green-400" />
                Flexible Withdrawals
              </h3>
              <p className="text-slate-300 text-sm">
                Access your funds whenever you need them. No lock-in periods or penalties.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <CheckCircle size={20} className="text-green-400" />
                Transparent Operations
              </h3>
              <p className="text-slate-300 text-sm">
                Track your investments in real-time with detailed reports and analytics.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <CheckCircle size={20} className="text-green-400" />
                Expert Management
              </h3>
              <p className="text-slate-300 text-sm">
                Your investments are managed by experienced professionals using proven strategies.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PeneInvestment;
